import {Perspectives} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {
    Panel,
    PanelGroup,
    PanelGroupId,
    PanelId,
    PanelKind
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {CodeEditorPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/codeEditor/CodeEditorPanel';
import {StringUtil} from '@mintdata/mint-kit/impl/util/StringUtil';
import bind from 'bind-decorator';
import * as React from 'react';
import IModelContentChangedEvent = monaco.editor.IModelContentChangedEvent;
import IStandaloneCodeEditor = monaco.editor.IStandaloneCodeEditor;

export type MonacoEditorRef = IStandaloneCodeEditor;
export type OnMonacoEditor = (monacoPage:IMonacoPage) => (void);

export interface IMonacoPage
{
    editor:MonacoEditorRef;
    monacoNS:any;
}

// Monaco notes:
// https://stackoverflow.com/questions/43058191/how-to-use-addextralib-in-monaco-with-an-external-type-definition
// https://microsoft.github.io/monaco-editor/api/interfaces/monaco.languages.typescript.languageservicedefaults.html

export class MonacoEntry
{
    public hasLoadBeenRequested = false;
    public page:IMonacoPage | null = null;
    public listeners:OnMonacoEditor[] = [];

    constructor(public panelGroupId:PanelGroupId)
    {
    }
}

export class MonacoEditorIFrameManager
{
    public static BDP_BRIDGE_ON_CODE_EDITOR_LOADED = `__bdpBridge_onCodeEditorLoaded`;

    private _panelGroupToMonacoEditor:Map<PanelGroupId, MonacoEntry> = new Map();

    @bind
    public init():void
    {
        const win = window as any;
        win[MonacoEditorIFrameManager.BDP_BRIDGE_ON_CODE_EDITOR_LOADED] = this._onCodeEditorIFrameLoaded;
    }

    @bind
    public getOrCreateEditorFor(panelGroup:PanelGroup, listener?:OnMonacoEditor):void
    {
        const panelGroupId = panelGroup.id;
        let entry:MonacoEntry = this._panelGroupToMonacoEditor.get(panelGroupId);

        if( !entry )
        {
            entry = new MonacoEntry(panelGroupId);
            this._panelGroupToMonacoEditor.set(panelGroupId, entry);
        }

        if( entry.page )
        {
            if( listener )
                listener(entry.page);
        }
        else
        {
            if( listener )
                entry.listeners.push(listener);

            if( !entry.hasLoadBeenRequested )
            {
                entry.hasLoadBeenRequested = true;
                this._registerHeavyPanel(panelGroup);
            }
        }
    }

    @bind
    private _registerHeavyPanel(panelGroup:PanelGroup):void
    {
        const perspective = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);

        if( !perspective )
        {
            console.log(`Error: perspective for FLOW_TAB not registered yet`);
            return;
        }

        const element = React.createElement(CodeEditorPanel, {codeEditorId: panelGroup.id});
        perspective.addHeavyPanelContents(panelGroup, PanelKind.FlowCodeEditor, element);
    }

    @bind
    private _onCodeEditorIFrameLoaded(panelGroupId:PanelGroupId,
                                      monacoEditor:MonacoEditorRef,
                                      monacoNS:any)
    {
        const entry = this._panelGroupToMonacoEditor.get(panelGroupId);
        entry.page = {editor: monacoEditor, monacoNS: monacoNS} as IMonacoPage;

        if( entry.listeners )
        {
            let listeners = entry.listeners.slice(0);
            entry.listeners = null;

            for( let listener of listeners )
                listener(entry.page);
        }
    }
}

export type OnCodeContentChanged = (newCodeContent:string) => (void);

export type PanelGroupListeners = Map<PanelId, OnCodeContentChanged>;

export class CodeEditorController
{
    private _refManager:MonacoEditorIFrameManager = new MonacoEditorIFrameManager();
    private _panelGroupToListeners:Map<PanelGroupId, PanelGroupListeners> = new Map();

    @bind
    public init()
    {
        this._refManager.init();
    }

    @bind
    public ensureCodeEditorAvailableIn(panelGroup:PanelGroup):void
    {
        this._refManager.getOrCreateEditorFor(panelGroup);
    }

    @bind
    private _addListener(panelGroupId:PanelGroupId, panelId:PanelId, listener:OnCodeContentChanged)
    {
        let listeners = this._panelGroupToListeners.get(panelGroupId);

        if( !listeners )
        {
            listeners = new Map<PanelId, OnCodeContentChanged>() as PanelGroupListeners;
            this._panelGroupToListeners.set(panelGroupId, listeners);
        }

        listeners.set(panelId, listener);
    }

    @bind
    private _fireChangeEvent(panelGroup:PanelGroup, panel:Panel, newCodeContent:string):void
    {
        const listeners = this._panelGroupToListeners.get(panelGroup.id);
        if( !listeners )
            return;

        const listener = listeners.get(panel.id);
        if( listener )
        {
            const controller = controllers.ui.panels.general;
            const isSelectedAndActive = controller.isPanelSelectedAndActive(panelGroup, panel);

            if( isSelectedAndActive )
                listener(newCodeContent);
        }
    }

    @bind
    public setContent(panel:Panel, content:string, listener?:OnCodeContentChanged)
    {
        const panelGroup = panel.panelGroup;

        if( listener )
            this._addListener(panel.panelGroup.id, panel.id, listener);

        this._refManager.getOrCreateEditorFor(panelGroup, (page:IMonacoPage) =>
        {
            this._applyValueToEditor(panelGroup, panel, page, content);
        });
    }

    @bind
    private _applyValueToEditor(panelGroup:PanelGroup,
                                panel:Panel,
                                page:IMonacoPage,
                                scriptValue:string):void
    {
        const monaco = page.monacoNS;
        const editor = page.editor;

        const model = monaco.editor.createModel(scriptValue, 'typescript', null);
        model.setValue(scriptValue);

        editor.setModel(model);

        const lastPos = model.getPositionAt(scriptValue.length);
        editor.setPosition(lastPos);

        editor.onDidChangeModelContent((e:IModelContentChangedEvent) =>
        {
            const currentEditorContent = editor.getValue();
            this._fireChangeEvent(panelGroup, panel, currentEditorContent);

            e.changes.map((change:any) =>
            {
                const range = change.range;
                const rangeLength = change.rangeLength;
                const text = change.text;

                console.log(`start: ${range.startColumn} end: ${range.endColumn} len: ${rangeLength} text: ${text}`);
            });

        });

        (editor as any).onDidPaste(() =>
        {
            const content = editor.getValue();
            const cursorPosition = editor.getPosition();

            editor.setValue(StringUtil.replaceUnicodeQuotesIntoASCII(content));
            editor.setPosition(cursorPosition);
        });

        setTimeout(() =>
        {
            editor.focus();
        }, 0);
    }
}
